import { z } from 'zod'
import {
  dateRangeRefinement,
  eventLocationCreateSchema,
  eventPriceInfoSchema,
  nonEmptyString,
  occurrenceStartsUniqueRefinement,
  requireAddressParts,
  taxonomyTagsSchema,
  utcDateTimeString,
} from '../validation'
import { dropTeaserDtoSchema } from './drop'
import {
  eventDescriptionLanguageSchema,
  eventDescriptionTranslationsSchema,
} from './event-description'
import { eventRegistrationSummarySchema } from './event-registration'
import { imageSchema } from './image'
import { liveRoomFunnelCountsSchema, liveRoomModeValues } from './live-room'
import { locationSchema } from './location'
import { windowedQuerySchema, windowedResponseSchema } from './pagination'
import { eventRoomVisibilityValues } from './room-requests'
import { groupedTagsSchema } from './tag'

export const eventPriceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('unknown') }),
  z.object({ kind: z.literal('free') }),
  z.object({ kind: z.literal('fixed'), priceFixed: z.number() }),
  z.object({ kind: z.literal('range'), priceRangeMin: z.number(), priceRangeMax: z.number() }),
])

export type EventPrice = z.infer<typeof eventPriceSchema>

/** What an event's external link is for. */
export const eventTicketUrlKindValues = ['tickets', 'registration'] as const
export type EventTicketUrlKind = (typeof eventTicketUrlKindValues)[number]

export const eventOccurrenceSchema = z.object({
  id: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  isSoldOut: z.boolean(),
  isAllDay: z.boolean(),
  location: locationSchema,
  ticketUrl: z.string().nullable(),
})

export type EventOccurrence = z.infer<typeof eventOccurrenceSchema>

/**
 * A single dated time range authored for a recurring event. `startTime`/`endTime`
 * are UTC (overnight ranges have the end already rolled onto the next day so
 * `startTime < endTime` holds). Dashboard-authored occurrences inherit the parent
 * event's location and ticket URL.
 */
export const occurrenceInputSchema = z
  .strictObject({
    startTime: utcDateTimeString,
    endTime: utcDateTimeString,
    isAllDay: z.boolean().optional().default(false),
  })
  .superRefine(dateRangeRefinement)

export type OccurrenceInput = z.infer<typeof occurrenceInputSchema>

export const MAX_OCCURRENCES = 200

/** The age limits an organizer can set on an event. */
export const eventMinAgeValues = [16, 18] as const
export type EventMinAge = (typeof eventMinAgeValues)[number]

/** The most extra guests one "Bin dabei" may bring. */
export const MAX_PLUS_ONES = 10

const eventMinAgeSchema = z.union([z.literal(16), z.literal(18)])
const targetAgeSchema = z.number().int().min(14).max(99)

export const eventRsvpCountsSchema = z.object({
  going: z.number(),
  maybe: z.number(),
  interested: z.number(),
  notGoing: z.number(),
  /** Extra guests the "Bin dabei" answers bring along. */
  plusOnes: z.number().optional(),
})
export type EventRsvpCounts = z.infer<typeof eventRsvpCountsSchema>

export const eventDtoSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  listingId: z.string().nullable(),
  /** The organization's profile hosting the event; null for imported events. */
  hostProfileId: z.string().nullable().optional(),
  title: z.string(),
  description: z.string(),
  /** The language the organizer wrote `description` in. */
  descriptionLanguage: eventDescriptionLanguageSchema.default('de'),
  /** The description in further languages; see `eventDescriptionFor`. */
  descriptionTranslations: eventDescriptionTranslationsSchema.default({}),
  location: locationSchema,
  /** The venue is announced later: the location holds only the city, pinned at its centre. */
  locationPending: z.boolean().default(false),
  /**
   * Only guests who bought a ticket, were signed up or were let in see the address; the location
   * then holds only the city and rough coordinates, GET /api/events/{id}/address has the rest.
   */
  addressHidden: z.boolean().default(false),
  startTime: z.string(),
  endTime: z.string(),
  price: eventPriceSchema,
  ticketUrl: z.string().nullable(),
  /** What `ticketUrl` is for: buying tickets, or signing up ("Anmelden"). */
  ticketUrlKind: z.enum(eventTicketUrlKindValues).default('tickets'),
  capacity: z.number().nullable(),
  /** The organizer collects "Bin dabei" / "Vielleicht" instead of a plain "Interessiert". */
  rsvpEnabled: z.boolean().default(false),
  /** What the event's Live Room offers: nothing, only a head count, or open people with Hi. */
  liveRoomMode: z.enum(liveRoomModeValues).default('open'),
  /** `request`: listed for everyone, people get in once the organizer accepted them; null is open. */
  roomVisibility: z.enum(eventRoomVisibilityValues).nullable().optional(),
  /** The organizer gets bundled emails about new requests; only on the business's own detail. */
  requestEmails: z.boolean().optional(),
  /** Extra guests one "Bin dabei" may bring; 0 means none. */
  plusOnesMax: z.number().int().optional(),
  /** The youngest age allowed in: null for everyone. */
  minAge: eventMinAgeSchema.nullable().optional(),
  /** Only students get in; a student ID is checked at the door. */
  studentsOnly: z.boolean().optional(),
  /** Only active members of the hosting organizer see the event ("Nur Mitglieder"). */
  membersOnly: z.boolean().optional(),
  /** The organizer's own words on who the event is for. */
  audienceNote: z.string().nullable().optional(),
  /** The age range the organizer has in mind; a hint, never a gate. */
  targetAgeMin: z.number().nullable().optional(),
  targetAgeMax: z.number().nullable().optional(),
  /** The short link name: the event page lives at `/events/{slug}` when it has one. */
  slug: z.string().nullable().optional(),
  /** Only on the business's own event detail, and only for an RSVP event. */
  /** Anonymous answer counts for the organizer; hidden bookmarks never count. */
  rsvpCounts: eventRsvpCountsSchema.optional(),
  /** How far people got in the Live Room; only on the organizer's own detail. */
  liveRoomFunnel: liveRoomFunnelCountsSchema.optional(),
  interestedCount: z.number(),
  status: z.enum(['importing', 'draft', 'published', 'unlisted']),
  isDraft: z.boolean(),
  isCanceled: z.boolean(),
  tags: groupedTagsSchema,
  images: z.array(imageSchema),
  coverImageUrl: z.string().nullable(),
  occurrences: z.array(eventOccurrenceSchema).default([]),
  /** Sign-up ("Anmeldung") summary; only on the detail responses, null when it is off. */
  registration: eventRegistrationSummarySchema.nullable().optional(),
  /** Live drop linked to this event; only set on the public detail response. */
  liveDrop: dropTeaserDtoSchema.nullable().optional(),
  /** Search-index facts; only set on the public detail response. */
  seo: z
    .object({
      /** Whether the page belongs in search indexes and sitemaps. */
      indexable: z.boolean(),
      /** Came in through a feed import; its description is the source's text. */
      imported: z.boolean(),
      planCount: z.number().int(),
      hasLiveRoom: z.boolean(),
    })
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type EventDto = z.infer<typeof eventDtoSchema>

function eventLocationRefinement(
  data: {
    location?: z.infer<typeof eventLocationCreateSchema> | null
    locationPending?: boolean
  },
  ctx: z.RefinementCtx,
) {
  if (!data.location) return
  const fields = data.locationPending
    ? (['city', 'country'] as const)
    : (['street', 'city', 'country'] as const)
  requireAddressParts(data.location, ctx, fields, ['location'])
}

function targetAgeRangeRefinement(
  data: { targetAgeMin?: number | null; targetAgeMax?: number | null },
  ctx: z.RefinementCtx,
) {
  if (
    data.targetAgeMin != null &&
    data.targetAgeMax != null &&
    data.targetAgeMin > data.targetAgeMax
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['targetAgeMax'],
      message: 'targetAgeMax must not be below targetAgeMin',
    })
  }
}

export const createEventRequestSchema = z
  .strictObject({
    title: nonEmptyString,
    description: z.string(),
    descriptionLanguage: eventDescriptionLanguageSchema.optional(),
    descriptionTranslations: eventDescriptionTranslationsSchema.optional(),
    location: eventLocationCreateSchema.nullable().optional(),
    /** The venue is announced later: `location` then needs only the city and country. */
    locationPending: z.boolean().optional(),
    addressHidden: z.boolean().optional(),
    startTime: utcDateTimeString,
    endTime: utcDateTimeString,
    occurrences: z.array(occurrenceInputSchema).max(MAX_OCCURRENCES).optional(),
    price: eventPriceInfoSchema,
    ticketUrl: z.string().nullable().optional(),
    ticketUrlKind: z.enum(eventTicketUrlKindValues).optional(),
    capacity: z.number().int().min(0).nullable().optional(),
    rsvpEnabled: z.boolean().optional(),
    liveRoomMode: z.enum(liveRoomModeValues).optional(),
    roomVisibility: z.enum(eventRoomVisibilityValues).nullable().optional(),
    requestEmails: z.boolean().optional(),
    plusOnesMax: z.number().int().min(0).max(MAX_PLUS_ONES).optional(),
    minAge: eventMinAgeSchema.nullable().optional(),
    studentsOnly: z.boolean().optional(),
    membersOnly: z.boolean().optional(),
    audienceNote: z.string().trim().max(200).nullable().optional(),
    targetAgeMin: targetAgeSchema.nullable().optional(),
    targetAgeMax: targetAgeSchema.nullable().optional(),
    listingId: z.string().uuid().nullable().optional(),
    /** The organization's profile hosting the event; omitted picks the place's or the organizer page. */
    hostProfileId: z.string().uuid().optional(),
    tags: taxonomyTagsSchema,
  })
  .superRefine((data, ctx) => {
    dateRangeRefinement(data, ctx)
    occurrenceStartsUniqueRefinement(data.occurrences, ctx)
    targetAgeRangeRefinement(data, ctx)
    eventLocationRefinement(data, ctx)
    if (data.location == null && (data.listingId == null || data.locationPending)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['location'],
        message: data.locationPending
          ? 'A venue announced later still needs its city'
          : 'Location is required when no listing is specified',
      })
    }
  })

export const updateEventRequestSchema = z
  .strictObject({
    title: nonEmptyString.optional(),
    description: z.string().optional(),
    descriptionLanguage: eventDescriptionLanguageSchema.optional(),
    descriptionTranslations: eventDescriptionTranslationsSchema.optional(),
    location: eventLocationCreateSchema.optional(),
    locationPending: z.boolean().optional(),
    addressHidden: z.boolean().optional(),
    startTime: utcDateTimeString.optional(),
    endTime: utcDateTimeString.optional(),
    occurrences: z.array(occurrenceInputSchema).max(MAX_OCCURRENCES).optional(),
    price: eventPriceInfoSchema.optional(),
    ticketUrl: z.string().nullable().optional(),
    ticketUrlKind: z.enum(eventTicketUrlKindValues).optional(),
    capacity: z.number().int().min(0).nullable().optional(),
    rsvpEnabled: z.boolean().optional(),
    liveRoomMode: z.enum(liveRoomModeValues).optional(),
    roomVisibility: z.enum(eventRoomVisibilityValues).nullable().optional(),
    requestEmails: z.boolean().optional(),
    plusOnesMax: z.number().int().min(0).max(MAX_PLUS_ONES).optional(),
    minAge: eventMinAgeSchema.nullable().optional(),
    studentsOnly: z.boolean().optional(),
    membersOnly: z.boolean().optional(),
    audienceNote: z.string().trim().max(200).nullable().optional(),
    targetAgeMin: targetAgeSchema.nullable().optional(),
    targetAgeMax: targetAgeSchema.nullable().optional(),
    hostProfileId: z.string().uuid().optional(),
    tags: taxonomyTagsSchema.optional(),
  })
  .superRefine((data, ctx) => {
    dateRangeRefinement(data, ctx)
    occurrenceStartsUniqueRefinement(data.occurrences, ctx)
    targetAgeRangeRefinement(data, ctx)
    eventLocationRefinement(data, ctx)
  })

export const eventStatusActionRequestSchema = z.strictObject({
  action: z.enum(['publish', 'unlist', 'cancel']),
})

export const eventCreatedResponseSchema = z.object({
  id: z.string().uuid(),
})

export const eventListResponseSchema = windowedResponseSchema(eventDtoSchema)

export const eventListQuerySchema = windowedQuerySchema.extend({
  when: z.enum(['upcoming', 'past']),
  /** Only the events this profile hosts; ignored while organization profiles are off. */
  hostProfileId: z.string().uuid().optional(),
  /** Words in the title, description or place name; case and accents do not matter. */
  q: z.string().trim().max(200).optional(),
  /** With `to`: only the events whose time overlaps this range. */
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
})
export type EventListQuery = z.infer<typeof eventListQuerySchema>

export type CreateEventRequest = z.infer<typeof createEventRequestSchema>
export type UpdateEventRequest = z.infer<typeof updateEventRequestSchema>
export type EventCreatedResponse = z.infer<typeof eventCreatedResponseSchema>
export type EventListResponse = z.infer<typeof eventListResponseSchema>

/** GET /api/events/{id}/address: the full place of an event, for whoever may see it. */
export const eventAddressResponseSchema = z.object({ location: locationSchema })
export type EventAddressResponse = z.infer<typeof eventAddressResponseSchema>
